import React, {useEffect, useRef, useState} from 'react';
import InputField from "../../../../shared/ui/field/InputField";
import styled from "styled-components";
import {GLOBAL_STYLE_CONFIG} from "../../../../app/styles/GlobalStyles";

interface IProps {
    /**
     * Значение поля поиска (по умолчанию, начальное значение)
     * @type {string}
     */
    token: string;

    /**
     * Подсказка для автозаполнения (autocomplete suggestion)
     * @type {string}
     */
    suggest: string,

    /**
     * Функция для получения товаров по поисковому запросу
     * @param searchTerm - строка запроса
     */
    getProductsBySearch: (searchTerm: string) => void
}


const StyledAutocomplete = styled.div`
    flex: 1;
    position: relative;
    width: max-content;
    max-width: 100%;
    margin: var(${GLOBAL_STYLE_CONFIG.variable('search', 'input-wrapper-margin')}, clamp(10px, 2vw, 15px) clamp(10px, 2vw, 15px) clamp(10px, 2vw, 15px) clamp(15px, 4vw, 40px));

    .${GLOBAL_STYLE_CONFIG.splitByPrefix('search-input')} {
        font-size: var(${GLOBAL_STYLE_CONFIG.variable('search', 'font-size')}, clamp(16px, 2vw, 18px));
        font-weight: var(${GLOBAL_STYLE_CONFIG.variable('search', 'font-weight')}, 400);
        line-height: 120%;
        font-family: var(${GLOBAL_STYLE_CONFIG.variable('search', 'font-family')}, inherit);

        color: var(${GLOBAL_STYLE_CONFIG.variable('search', 'text-color')}, ${({theme}) => theme.search.textColor});
        padding: 0;
        margin: 0;
        display: block;

    }

`

const StyledSuggestBlock = styled.div`

    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    pointer-events: none;
    white-space: nowrap;
    overflow: hidden;
    text-align: left;
    font-size:var(${GLOBAL_STYLE_CONFIG.variable('search', 'font-size')}, clamp(16px, 2vw, 18px));
    font-weight:var(${GLOBAL_STYLE_CONFIG.variable('search', 'font-weight')}, 400);
    line-height: 120%;
    font-family: var(${GLOBAL_STYLE_CONFIG.variable('search', 'font-family')}, inherit);
    //line-height: clamp(18px, 3vw, 22px);
    color: var(${GLOBAL_STYLE_CONFIG.variable('search', 'suggest-color')}, ${({theme}) => theme.search.suggestColor});



    .${GLOBAL_STYLE_CONFIG.splitByPrefix('search-suggest-invisible')} {
        visibility: hidden;
    }


`

/**
 * `AutocompleteInput` — компонент, который отвечает за:
 * - отображение поля ввода поиска
 * - показ автозаполняемой подсказки
 * - запуск поиска товаров по onChange-значению через отложений тамер
 *
 * @component
 * @returns {JSX.Element} Компонент поля ввода с автозаполнением
 */

const AutocompleteInput: React.FC<IProps> = ({token, suggest, getProductsBySearch}) => {
    const [inputValue, setInputValue] = useState(token);
    const debounceRef = useRef<any>(null);
    /**
     *
     * запуск поиска товаров по onChange-значению через отложений тамер
     */

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setInputValue(value);
        //
        // if (debounceRef.current) clearTimeout(debounceRef.current);
        //
        debounceRef.current = setTimeout(() => {
            getProductsBySearch(value);
        }, 50);
    };

    /**
     *  Возвращает часть строки автозаполнения
     *
     * @returns {string} - текст автокомплита (если применимо)
     */
    const getAutocomplete = () => {
        if (
            suggest.toLowerCase().startsWith(inputValue.toLowerCase()) &&
            inputValue.length > 0
        ) {
            return suggest.slice(inputValue.length);
        }
        return '';
    };
    /**
     * При нажатии Tab или → вставляет автокомплит в поле ввода
     * @param {React.KeyboardEvent<HTMLInputElement>} e - событие клавиатуры
     */
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const autocompletePart = getAutocomplete();
        if ((e.key === 'Tab' || e.key === 'ArrowRight') && autocompletePart) {
            e.preventDefault(); // предотвращаем фокус или перемещение курсора
            setInputValue(inputValue + autocompletePart);
            getProductsBySearch(inputValue + autocompletePart);
        }
    };


    // синхронизация токена
    useEffect(() => {
        if (token !== inputValue) {
            setInputValue(token);
        }
    }, [token]);

    return (
        <StyledAutocomplete className={GLOBAL_STYLE_CONFIG.splitByPrefix('search-autocomplete')}>
            {/* Подсказка (background-текст) */}
            <StyledSuggestBlock className={GLOBAL_STYLE_CONFIG.splitByPrefix('search-suggest')}>
                <span className={GLOBAL_STYLE_CONFIG.splitByPrefix('search-suggest-invisible')}>{inputValue}</span>
                <span className={GLOBAL_STYLE_CONFIG.splitByPrefix('search-suggest-text')}>{getAutocomplete()}</span>
            </StyledSuggestBlock>

            {/* Ввод пользователя */}
            <InputField type={"text"} value={inputValue}
                        onChange={handleChange}
                        placeholder={'Я шукаю'}
                        onKeyDown={handleKeyDown}
                        className={GLOBAL_STYLE_CONFIG.splitByPrefix('search-input')}

            />
        </StyledAutocomplete>
    );
};

export default AutocompleteInput;